import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { MigratedStudent, StudentsApi } from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Move students between sections within their CURRENT academic year — a new
 * enrollment row is created and the old one is retired (is_latest_placement),
 * never edited in place; see `StudentsService.transfer()`. Deliberately does
 * NOT touch fees — a section move has no automatic fee-demand side effect
 * (legacy's ClassChangeController coupled the two and corrupted real payment
 * data doing it; see docs/data-issues.md). Use the Fees screens directly if a
 * transfer should also change what a student owes.
 */
export function TransferStudentsPage() {
  const qc = useQueryClient();
  const term = useTerminology();
  const { can } = usePermissions();
  const canTransfer = can('/students', 'create');

  const [fromClassId, setFromClassId] = useState('');
  const [fromSectionId, setFromSectionId] = useState('');
  const [toClassId, setToClassId] = useState('');
  const [toSectionId, setToSectionId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ transferred: number } | null>(null);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'transfer-page'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const fromSections = classes.find((c) => c.id === fromClassId)?.sections ?? [];
  const toSections = classes.find((c) => c.id === toClassId)?.sections ?? [];

  const { data: studentsPage, isFetching: loadingStudents } = useQuery({
    queryKey: ['students', 'transfer-from-section', fromSectionId],
    queryFn: () => StudentsApi.list({ sectionId: fromSectionId, limit: 100, sortBy: 'rollNumber', sortOrder: 'asc' }),
    enabled: !!fromSectionId,
  });
  const students = (studentsPage?.items ?? []).filter((s) => s.enrolled);

  useEffect(() => {
    setSelected(new Set());
    setResult(null);
    setError(null);
  }, [fromSectionId]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = students.length > 0 && students.every((s) => selected.has(s.id));
  const toggleAll = () =>
    setSelected((prev) => {
      if (allSelected) return new Set();
      return new Set(students.map((s) => s.id));
    });

  const transfer = useMutation({
    mutationFn: () =>
      StudentsApi.transfer({
        studentIds: [...selected],
        fromSectionId,
        toSectionId,
      }),
    onSuccess: (r) => {
      setResult({ transferred: r.transferred });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.error?.message ?? 'Transfer failed');
    },
  });

  const ready = !!fromSectionId && !!toSectionId && fromSectionId !== toSectionId && selected.size > 0;

  return (
    <div>
      <PageHeader
        title="Transfer Students"
        description={`Move students to a different ${term.group.toLowerCase()} within the current academic year. Fees are untouched — use the Fees screens if a transfer should also change what a student owes.`}
      />

      {!canTransfer && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          You don't have permission to transfer students.
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}
      {result && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          Transferred {result.transferred} student(s).
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm md:grid-cols-[1fr_auto_1fr]">
        <div className="grid grid-cols-2 gap-4">
          <Field label={term.level} required>
            <Select
              value={fromClassId}
              onChange={(e) => {
                setFromClassId(e.target.value);
                setFromSectionId('');
              }}
            >
              <option value="">Select</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={term.group} required>
            <Select value={fromSectionId} onChange={(e) => setFromSectionId(e.target.value)} disabled={!fromClassId}>
              <option value="">Select</option>
              {fromSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="hidden items-center justify-center md:flex">
          <ArrowRight className="h-5 w-5 text-slate-400" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={`To ${term.level}`} required>
            <Select
              value={toClassId}
              onChange={(e) => {
                setToClassId(e.target.value);
                setToSectionId('');
              }}
            >
              <option value="">Select</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={`To ${term.group}`} required>
            <Select value={toSectionId} onChange={(e) => setToSectionId(e.target.value)} disabled={!toClassId}>
              <option value="">Select</option>
              {toSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {fromSectionId && fromSectionId === toSectionId && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Source and destination {term.group.toLowerCase()} must be different.
        </div>
      )}

      {fromSectionId && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-700">
              Students {loadingStudents ? '' : `(${students.length})`}
            </h3>
            {students.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                Select all
              </label>
            )}
          </div>
          {loadingStudents ? (
            <div className="p-6 text-sm text-slate-400">Loading…</div>
          ) : students.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No students currently in this {term.group.toLowerCase()}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
                  <th className="w-10 px-4 py-2" />
                  <th className="px-4 py-2">Admission #</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Roll #</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s: MigratedStudent) => (
                  <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                    </td>
                    <td className="px-4 py-2">{s.admissionNo}</td>
                    <td className="px-4 py-2">{s.name ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2">{s.rollNumber ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!canTransfer || !ready || transfer.isPending}
          onClick={() => {
            setError(null);
            setResult(null);
            transfer.mutate();
          }}
        >
          {transfer.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
          Transfer {selected.size > 0 ? `(${selected.size})` : ''}
        </button>
      </div>
    </div>
  );
}
